import React, { useState, useEffect } from 'react';

const GRAFANA_BASE = process.env.REACT_APP_GRAFANA_URL || 'https://grafana.digitransolutions.in:3000';

const DASHBOARDS = [
  { title: 'BIW Overview',       uid: 'biw-overview',          icon: '🏭', desc: 'Robot cell 3D view + live telemetry' },
  { title: 'Robot Cell 1',       uid: 'biw-robot-cell-1',      icon: '🤖', desc: 'Joint angles, clamp force, cycle time' },
  { title: 'Collision Alerts',   uid: 'biw-collision-alerts',  icon: '⚠️', desc: 'Collision severity timeline & log' },
  { title: 'Design Validation',  uid: 'biw-design-validation', icon: '✅', desc: 'Validation rule pass/fail results' },
  { title: 'Executive Summary',  uid: 'biw-executive-summary', icon: '📊', desc: 'KPIs, OEE, production counts' },
  { title: 'Fault Monitoring',   uid: 'biw-fault-monitoring',  icon: '🔴', desc: 'Active faults, fault timeline, recovery' },
];

// ── Connection status dot ─────────────────────────────────────────────────────
function StatusDot({ url }) {
  const [status, setStatus] = useState('checking'); // 'checking' | 'connected' | 'unreachable'

  useEffect(() => {
    // no-cors means we can't read the response body, but a resolved promise
    // means the server is reachable
    fetch(`${url}/api/health`, { mode: 'no-cors' })
      .then(() => setStatus('connected'))
      .catch(() => setStatus('unreachable'));
  }, [url]);

  const color = { checking: '#aaa', connected: '#27ae60', unreachable: '#c0392b' }[status];

  return (
    <span style={{ color, fontWeight: 600, fontSize: 13 }}>
      ● {status}
    </span>
  );
}

// ── Dashboard card ────────────────────────────────────────────────────────────
function DashboardCard({ title, uid, icon, desc }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={`${GRAFANA_BASE}/d/${uid}`}
      target="_blank"
      rel="noreferrer"
      style={{
        ...styles.card,
        boxShadow: hovered ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
        transform: hovered ? 'translateY(-2px)' : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={styles.icon}>{icon}</span>
      <div>
        <div style={styles.cardTitle}>{title}</div>
        <div style={styles.cardDesc}>{desc}</div>
      </div>
    </a>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function GrafanaLink() {
  const [activeEmbed, setActiveEmbed] = useState('biw-fault-monitoring');

  const EMBEDS = [
    { label: '🔴 Fault Monitoring',  uid: 'biw-fault-monitoring'  },
    { label: '🏭 Overview',          uid: 'biw-overview'          },
    { label: '🤖 Robot Cell 1',      uid: 'biw-robot-cell-1'      },
    { label: '⚠️ Collision Alerts',  uid: 'biw-collision-alerts'  },
  ];

  return (
    <div style={styles.page}>

      {/* ── Header ── */}
      <div style={styles.header}>
        <h2 style={{ margin: 0 }}>📡 Live Grafana Dashboards</h2>
        <a href={GRAFANA_BASE} target="_blank" rel="noreferrer" style={styles.openBtn}>
          Open Grafana ↗
        </a>
      </div>

      {/* ── Connection status ── */}
      <p style={styles.sub}>
        Connected to: <code style={styles.code}>{GRAFANA_BASE}</code>
        &nbsp;&nbsp;
        <StatusDot url={GRAFANA_BASE} />
      </p>

      {/* ── Dashboard cards grid ── */}
      <div style={styles.grid}>
        {DASHBOARDS.map(d => (
          <DashboardCard key={d.uid} {...d} />
        ))}
      </div>

      {/* ── Embedded iframe section ── */}
      <div style={styles.embedSection}>
        {/* Tab selector */}
        <div style={styles.embedTabs}>
          {EMBEDS.map(e => (
            <button
              key={e.uid}
              onClick={() => setActiveEmbed(e.uid)}
              style={{
                ...styles.embedTab,
                background: activeEmbed === e.uid ? '#0073bb' : 'transparent',
                color:      activeEmbed === e.uid ? '#fff'    : '#aaa',
              }}
            >
              {e.label}
            </button>
          ))}
        </div>

        {/* iframe */}
        <iframe
          key={activeEmbed}
          src={`${GRAFANA_BASE}/d/${activeEmbed}?orgId=1&refresh=10s&kiosk`}
          width="100%"
          height="520"
          frameBorder="0"
          title={activeEmbed}
          style={styles.iframe}
        />

        <p style={styles.iframeNote}>
          ℹ️ If the dashboard is not visible,{' '}
          <a href={GRAFANA_BASE} target="_blank" rel="noreferrer" style={{ color: '#7ec8e3' }}>
            open Grafana
          </a>{' '}
          in a new tab to authenticate, then return here.
        </p>
      </div>

    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  page:        { maxWidth: 980, margin: '0 auto', padding: 24, fontFamily: 'sans-serif' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  openBtn:     { padding: '8px 16px', background: '#e67e22', color: '#fff', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 14 },
  sub:         { color: '#666', fontSize: 13, marginBottom: 20 },
  code:        { background: '#eee', padding: '2px 6px', borderRadius: 4, fontSize: 12 },
  grid:        { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 },
  card:        { display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, textDecoration: 'none', color: '#333', transition: 'box-shadow 0.2s, transform 0.2s' },
  icon:        { fontSize: 28, flexShrink: 0 },
  cardTitle:   { fontWeight: 600, fontSize: 14, marginBottom: 2 },
  cardDesc:    { fontSize: 12, color: '#888' },
  embedSection:{ background: '#1a1a2e', borderRadius: 8, padding: 16 },
  embedTabs:   { display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' },
  embedTab:    { padding: '6px 14px', border: '1px solid #444', borderRadius: 4, cursor: 'pointer', fontSize: 13, transition: 'background 0.2s' },
  iframe:      { borderRadius: 6, display: 'block', background: '#000' },
  iframeNote:  { color: '#888', fontSize: 12, marginTop: 10, marginBottom: 0 },
};
